/**
 * 🔑 FORGOT PASSWORD LAYER
 * 
 * Sistema completo de reset de senha com 3 etapas:
 * 1. Digitar email
 * 2. Verificar código (modal igual ao 2FA)
 * 3. Definir nova senha
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { API_URL } from '../config/api';
import PasswordResetModal from './modals/PasswordResetModal';
import PagmusLogo from '../assets/logo/PagmuslogoNegativa.svg';
import './ForgotPasswordLayer.scss';

const ForgotPasswordLayer = () => {
  // Estado das etapas
  const [step, setStep] = useState(1); // 1: Email, 2: Código, 3: Nova Senha
  
  // Etapa 1: Email
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Etapa 2: Código
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [codeExpiresIn, setCodeExpiresIn] = useState(600);
  const [codeVerified, setCodeVerified] = useState(false);
  
  // Etapa 3: Nova Senha
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  /**
   * Etapa 1: Solicitar código de reset
   */
  const handleSendResetCode = async () => {
    if (!email || !email.includes('@')) {
      setError('Digite um email válido');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setCodeExpiresIn(data.expiresIn || 600);
        setShowCodeModal(true);
        setStep(2);
      } else {
        setError(data.message || 'Erro ao enviar código');
      }
    } catch (error) {
      console.error('Erro ao enviar código:', error);
      setError('Erro ao enviar código de verificação');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Etapa 2: Verificar código
   */
  const handleVerifyCode = async (code) => {
    try {
      // Garantir que o código seja string e tenha 6 dígitos
      const codeString = String(code).trim();
      
      console.log('🔍 [ForgotPassword] Verificando código:', { email, codeLength: codeString.length, code: codeString.substring(0, 3) + '***' });

      if (!/^\d{6}$/.test(codeString)) {
        return {
          success: false,
          message: 'Código deve ter 6 dígitos numéricos'
        };
      }

      const response = await fetch(`${API_URL}/api/auth/verify-reset-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: email.toLowerCase().trim(), 
          code: codeString 
        })
      });

      // Verificar se a resposta é JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ [ForgotPassword] Resposta não é JSON:', text);
        return {
          success: false,
          message: `Erro ao verificar código: ${response.status} ${response.statusText}`
        };
      }

      const data = await response.json();

      if (data.success) {
        setCodeVerified(true);
        // Mudar para etapa 3 imediatamente após código validado
        setShowCodeModal(false);
        setStep(3);
        return { success: true };
      } else {
        return {
          success: false,
          message: data.message,
          attemptsLeft: data.attemptsLeft
        };
      }
    } catch (error) {
      console.error('❌ [ForgotPassword] Erro ao verificar código:', error);
      return {
        success: false,
        message: error.message || 'Erro ao verificar código. Tente novamente.'
      };
    }
  };

  /**
   * Etapa 2: Reenviar código
   */
  const handleResendCode = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setCodeExpiresIn(data.expiresIn || 600);
        return { success: true, expiresIn: data.expiresIn || 600 };
      } else {
        return { success: false, message: data.message || 'Erro ao reenviar código' };
      }
    } catch (error) {
      console.error('Erro ao reenviar código:', error);
      return { success: false, message: 'Erro ao reenviar código de verificação' };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Etapa 3: Validar senha
   */
  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'A senha deve ter pelo menos 8 caracteres';
    }

    if (!/[A-Z]/.test(password)) {
      return 'A senha deve conter pelo menos 1 letra maiúscula';
    }

    if (!/[a-z]/.test(password)) {
      return 'A senha deve conter pelo menos 1 letra minúscula';
    }

    if (!/[0-9]/.test(password)) {
      return 'A senha deve conter pelo menos 1 número';
    }

    return null;
  };

  /**
   * Etapa 3: Resetar senha
   */
  const handleResetPassword = async () => {
    // Validar senha
    const passwordValidation = validatePassword(newPassword);
    if (passwordValidation) {
      setPasswordError(passwordValidation);
      return;
    }

    // Validar confirmação
    if (newPassword !== confirmPassword) {
      setPasswordError('As senhas não coincidem');
      return;
    }

    setLoading(true);
    setPasswordError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, newPassword })
      });

      const data = await response.json();

      if (data.success) {
        setPasswordSuccess(true);
        // Redirecionar para login após 2 segundos
        setTimeout(() => {
          window.location.href = '/#/sign-in';
        }, 2000);
      } else {
        setPasswordError(data.message || 'Erro ao resetar senha');
      }
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      setPasswordError('Erro ao resetar senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verificar critérios de senha em tempo real
   */
  const getPasswordCriteria = () => {
    const hasLength = newPassword.length >= 8;
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);

    return {
      length: hasLength,
      upperCase: hasUpperCase,
      lowerCase: hasLowerCase,
      number: hasNumber,
      allValid: hasLength && hasUpperCase && hasLowerCase && hasNumber
    };
  };

  const passwordCriteria = getPasswordCriteria();

  return (
    <>
      <section className="auth forgot-password-layer">
        <div className="auth-container">
          {/* Logo Pagmus - Aparece em todas as etapas */}
          <div className="brand-header">
            <img src={PagmusLogo} alt="Pagmus" className="brand-logo" />
          </div>

          {/* Etapa 1: Digitar Email */}
          {step === 1 && (
            <div className="auth-content">
              <div className="auth-header">
                <div className="auth-icon">
                  <Icon icon="mdi:lock-reset" />
                </div>
                <h2 className="auth-title">Esqueceu sua senha?</h2>
                <p className="auth-subtitle">
                  Digite seu email e enviaremos um código de verificação para você redefinir sua senha.
                </p>
              </div>

              <div className="auth-form">
                {error && (
                  <div className="auth-error">
                    <Icon icon="mdi:alert-circle" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <div className="input-wrapper">
                    <Icon icon="mdi:email" className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError('');
                      }}
                      placeholder="seu@email.com"
                      disabled={loading}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSendResetCode();
                        }
                      }}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSendResetCode}
                  disabled={loading || !email}
                  className="auth-button"
                >
                  {loading ? (
                    <>
                      <Icon icon="mdi:loading" className="spinner" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Icon icon="mdi:email-send" />
                      Enviar Código
                    </>
                  )}
                </button>

                <div className="auth-footer">
                  <Link to="/sign-in" className="auth-link">
                    <Icon icon="mdi:arrow-left" />
                    Voltar ao login
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Etapa 3: Nova Senha */}
          {step === 3 && (
            <div className="auth-content">
              <div className="auth-header">
                <div className="auth-icon">
                  <Icon icon="mdi:lock-reset" />
                </div>
                <h2 className="auth-title">Nova Senha</h2>
                <p className="auth-subtitle">
                  Digite sua nova senha. Certifique-se de que ela atenda a todos os critérios abaixo.
                </p>
              </div>

              <form 
                className="auth-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (passwordCriteria.allValid && newPassword === confirmPassword && !passwordSuccess) {
                    handleResetPassword();
                  }
                }}
              >
                {passwordSuccess && (
                  <div className="auth-success">
                    <Icon icon="mdi:check-circle" />
                    <span>Senha redefinida com sucesso! Redirecionando...</span>
                  </div>
                )}

                {passwordError && (
                  <div className="auth-error">
                    <Icon icon="mdi:alert-circle" />
                    <span>{passwordError}</span>
                  </div>
                )}

                {/* Critérios de senha */}
                <div className="password-criteria">
                  <div className={`criteria-item ${passwordCriteria.length ? 'valid' : ''}`}>
                    <Icon icon={passwordCriteria.length ? 'mdi:check-circle' : 'mdi:circle-outline'} />
                    <span>Mínimo de 8 caracteres</span>
                  </div>
                  <div className={`criteria-item ${passwordCriteria.upperCase ? 'valid' : ''}`}>
                    <Icon icon={passwordCriteria.upperCase ? 'mdi:check-circle' : 'mdi:circle-outline'} />
                    <span>1 letra maiúscula</span>
                  </div>
                  <div className={`criteria-item ${passwordCriteria.lowerCase ? 'valid' : ''}`}>
                    <Icon icon={passwordCriteria.lowerCase ? 'mdi:check-circle' : 'mdi:circle-outline'} />
                    <span>1 letra minúscula</span>
                  </div>
                  <div className={`criteria-item ${passwordCriteria.number ? 'valid' : ''}`}>
                    <Icon icon={passwordCriteria.number ? 'mdi:check-circle' : 'mdi:circle-outline'} />
                    <span>1 número</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">Nova Senha</label>
                  <div className="input-wrapper">
                    <Icon icon="mdi:lock" className="input-icon" />
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordError('');
                      }}
                      placeholder="Digite sua nova senha"
                      disabled={loading || passwordSuccess}
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirmar Senha</label>
                  <div className="input-wrapper">
                    <Icon icon="mdi:lock-check" className="input-icon" />
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordError('');
                      }}
                      placeholder="Digite novamente"
                      disabled={loading || passwordSuccess}
                      autoComplete="new-password"
                    />
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <div className="form-error">
                      <Icon icon="mdi:alert-circle" />
                      <span>As senhas não coincidem</span>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !passwordCriteria.allValid || newPassword !== confirmPassword || passwordSuccess}
                  className="auth-button"
                >
                  {loading ? (
                    <>
                      <Icon icon="mdi:loading" className="spinner" />
                      Redefinindo...
                    </>
                  ) : (
                    <>
                      <Icon icon="mdi:check-circle" />
                      Redefinir Senha
                    </>
                  )}
                </button>

                <div className="auth-footer">
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setEmail('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                      setPasswordSuccess(false);
                      setCodeVerified(false);
                    }}
                    className="auth-link-button"
                  >
                    <Icon icon="mdi:arrow-left" />
                    Voltar
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Modal de Código (Etapa 2) */}
      {/* Só renderizar modal se estiver na etapa 2 e código não foi validado ainda */}
      {step === 2 && !codeVerified && (
        <PasswordResetModal
          show={showCodeModal}
          onHide={() => {
            // Se modal foi fechado manualmente (X ou ESC), voltar para etapa 1
            setShowCodeModal(false);
            setStep(1);
          }}
          onVerify={handleVerifyCode}
          onResend={handleResendCode}
          email={email}
          expiresIn={codeExpiresIn}
        />
      )}
    </>
  );
};

export default ForgotPasswordLayer;
